import { Anchor, Badge, Card, Stack, Table, Text } from '@mantine/core';
import { Link } from 'react-router';
import { PageHeader } from '@/components/page-header';
import { SampleDataBanner } from '@/components/sample-data-banner';
import { FUNNEL_STEPS, type FunnelStepId } from '@/lib/analytics';
import { journeys } from '@/lib/fixtures/analytics';
import classes from './analytics-page.module.css';

/** How far a user got, coloured by how far that is. */
function ReachedBadge({ step }: { step: FunnelStepId }) {
  const label = FUNNEL_STEPS.find((entry) => entry.id === step)?.label ?? step;
  const colour =
    step === 'payment' || step === 'subscription'
      ? 'teal'
      : step === 'registration'
        ? 'brand'
        : 'gray';

  return (
    <Badge variant="light" color={colour} size="sm">
      {label}
    </Badge>
  );
}

export function JourneysPage() {
  return (
    <Stack gap="xl" className={classes.page}>
      <SampleDataBanner detail="Six invented users, chosen to cover the states that are awkward to represent rather than the states that are common." />

      <PageHeader
        title="Journeys"
        description="One user at a time, in order. At this scale journeys are read rather than sampled."
      />

      <Card p={0}>
        <Table.ScrollContainer minWidth={720}>
          <Table verticalSpacing="md" horizontalSpacing="lg" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th>Language</Table.Th>
                <Table.Th>Platform</Table.Th>
                <Table.Th>First run</Table.Th>
                <Table.Th>Reached</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {journeys.map((journey) => (
                <Table.Tr key={journey.id}>
                  <Table.Td>
                    <Stack gap={2}>
                      <Anchor
                        size="sm"
                        fw={600}
                        component={Link}
                        to={`/analytics/journeys/${journey.id}`}
                      >
                        {journey.label}
                      </Anchor>
                      <Text size="xs" c="dimmed">
                        {journey.headline}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{journey.language}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{journey.platform}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{journey.firstRunAt}</Text>
                  </Table.Td>
                  <Table.Td>
                    <ReachedBadge step={journey.reached} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Card>

      <Text size="xs" c="dimmed">
        Every user here started anonymous and device-scoped. The ones with an
        address on them adopted an account later; the events from before that
        point still belong to them.
      </Text>
    </Stack>
  );
}
